import React, { useState, useEffect } from 'react';
import {
  VStack, SimpleGrid, FormControl, FormLabel, Input, Select, Textarea,
  Button, HStack, Box, InputGroup, InputLeftAddon, Link, useToast,
} from '@chakra-ui/react';
import {
  ImmExpediente, crearExpediente, actualizarExpediente,
  listarLocalidades, Localidad,
} from '../../../api/immApi';
import { SectionTitle, IMM_COLOR, IMM_COLOR_HOVER } from '../_shared';
import MapSelector from '../../../components/MapSelector';

type Modo = 'new' | 'edit';

interface Props {
  modo: Modo;
  data?: ImmExpediente;
  onGuardado?: (exp: ImmExpediente) => void;
  onCancelar?: () => void;
}

const ESTADOS_CIVILES = ['Soltera', 'Casada', 'Unión libre', 'Divorciada', 'Viuda', 'Separada'];
const ESCOLARIDADES = ['Ninguna', 'Primaria', 'Secundaria', 'Bachillerato', 'Técnico', 'Licenciatura', 'Posgrado'];

const FormularioExpediente: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const [localidades, setLocalidades] = useState<Localidad[]>([]);
  const [guardando, setGuardando] = useState(false);

  const [f, setF] = useState<Partial<ImmExpediente>>({
    nombre: data?.nombre || '',
    apPaterno: data?.apPaterno || '',
    apMaterno: data?.apMaterno || '',
    fechaNacimiento: data?.fechaNacimiento ? data.fechaNacimiento.split('T')[0] : '',
    curp: data?.curp || '',
    estadoCivil: data?.estadoCivil || '',
    escolaridad: data?.escolaridad || '',
    ocupacion: data?.ocupacion || '',
    telefono: data?.telefono || '',
    celular: data?.celular || '',
    email: data?.email || '',
    estado: data?.estado || 'Tlaxcala',
    municipio: data?.municipio || 'Totolac',
    localidad: data?.localidad || '',
    colonia: data?.colonia || '',
    codigoPostal: data?.codigoPostal || '',
    tipoVialidad: data?.tipoVialidad || '',
    vialidad: data?.vialidad || '',
    numeroExterior: data?.numeroExterior || '',
    numeroInterior: data?.numeroInterior || '',
    referencias: data?.referencias || '',
    latitud: data?.latitud ?? undefined,
    longitud: data?.longitud ?? undefined,
    urlMaps: data?.urlMaps || '',
    numeroHijos: data?.numeroHijos ?? undefined,
    contactoEmergenciaNombre: data?.contactoEmergenciaNombre || '',
    contactoEmergenciaTelefono: data?.contactoEmergenciaTelefono || '',
    observaciones: data?.observaciones || '',
    estatus: data?.estatus || 'Activo',
  });

  useEffect(() => {
    listarLocalidades().then(setLocalidades).catch(() => setLocalidades([]));
  }, []);

  const set = (campo: keyof ImmExpediente, valor: any) => setF((prev) => ({ ...prev, [campo]: valor }));

  const handleUbicacion = (lat: number, lng: number, address: any) => {
    setF((prev) => ({
      ...prev,
      latitud: lat,
      longitud: lng,
      urlMaps: `https://www.google.com/maps?q=${lat},${lng}`,
      colonia: prev.colonia || address?.neighborhood || '',
      codigoPostal: prev.codigoPostal || address?.postalCode || '',
    }));
  };

  const handleGuardar = async () => {
    if (!f.nombre?.trim()) {
      toast({ title: 'El nombre es obligatorio', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const payload: Partial<ImmExpediente> = {
        ...f,
        latitud: f.latitud != null && String(f.latitud) !== '' ? Number(f.latitud) : undefined,
        longitud: f.longitud != null && String(f.longitud) !== '' ? Number(f.longitud) : undefined,
        numeroHijos: f.numeroHijos != null && String(f.numeroHijos) !== '' ? Number(f.numeroHijos) : undefined,
      };
      const res = modo === 'edit' && data
        ? await actualizarExpediente(data.id, payload)
        : await crearExpediente(payload);
      toast({ title: modo === 'edit' ? 'Expediente actualizado' : 'Expediente creado', status: 'success', duration: 3000 });
      onGuardado?.(res);
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo guardar el expediente.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Datos personales</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Nombre(s)</FormLabel>
          <Input value={f.nombre || ''} onChange={(e) => set('nombre', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Apellido paterno</FormLabel>
          <Input value={f.apPaterno || ''} onChange={(e) => set('apPaterno', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Apellido materno</FormLabel>
          <Input value={f.apMaterno || ''} onChange={(e) => set('apMaterno', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Fecha de nacimiento</FormLabel>
          <Input type="date" value={(f.fechaNacimiento as string) || ''} onChange={(e) => set('fechaNacimiento', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">CURP</FormLabel>
          <Input value={f.curp || ''} maxLength={18} textTransform="uppercase" onChange={(e) => set('curp', e.target.value.toUpperCase())} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estado civil</FormLabel>
          <Select placeholder="Selecciona" value={f.estadoCivil || ''} onChange={(e) => set('estadoCivil', e.target.value)}>
            {ESTADOS_CIVILES.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Escolaridad</FormLabel>
          <Select placeholder="Selecciona" value={f.escolaridad || ''} onChange={(e) => set('escolaridad', e.target.value)}>
            {ESCOLARIDADES.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Ocupación</FormLabel>
          <Input value={f.ocupacion || ''} onChange={(e) => set('ocupacion', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estatus</FormLabel>
          <Select value={f.estatus || 'Activo'} onChange={(e) => set('estatus', e.target.value)}>
            <option value="Activo">Activo</option>
            <option value="Inactivo">Inactivo</option>
          </Select>
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Contacto</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Teléfono</FormLabel>
          <Input value={f.telefono || ''} onChange={(e) => set('telefono', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Celular</FormLabel>
          <Input value={f.celular || ''} onChange={(e) => set('celular', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Correo electrónico</FormLabel>
          <Input type="email" value={f.email || ''} onChange={(e) => set('email', e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Domicilio</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Estado</FormLabel>
          <Input value={f.estado || ''} onChange={(e) => set('estado', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Municipio</FormLabel>
          <Input value={f.municipio || ''} onChange={(e) => set('municipio', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Localidad</FormLabel>
          <Select placeholder="Selecciona localidad" value={f.localidad || ''} onChange={(e) => {
            const loc = localidades.find((l) => l.descripcion === e.target.value);
            set('localidad', e.target.value);
            set('localidadId', loc?.id);
          }}>
            {localidades.map((l) => <option key={l.id} value={l.descripcion}>{l.descripcion}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Colonia</FormLabel>
          <Input value={f.colonia || ''} onChange={(e) => set('colonia', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Código postal</FormLabel>
          <Input value={f.codigoPostal || ''} onChange={(e) => set('codigoPostal', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Tipo de vialidad</FormLabel>
          <Input placeholder="Calle, Avenida, Privada..." value={f.tipoVialidad || ''} onChange={(e) => set('tipoVialidad', e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Vialidad (nombre)</FormLabel>
          <Input value={f.vialidad || ''} onChange={(e) => set('vialidad', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">No. exterior</FormLabel>
          <Input value={f.numeroExterior || ''} onChange={(e) => set('numeroExterior', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">No. interior</FormLabel>
          <Input value={f.numeroInterior || ''} onChange={(e) => set('numeroInterior', e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Referencias</FormLabel>
          <Input value={f.referencias || ''} onChange={(e) => set('referencias', e.target.value)} />
        </FormControl>
        <Box gridColumn={{ md: 'span 3' }}>
          <FormLabel fontSize="sm">Ubicación en el mapa</FormLabel>
          <MapSelector
            initialLat={f.latitud ?? undefined}
            initialLng={f.longitud ?? undefined}
            onLocationSelect={handleUbicacion}
          />
          {f.latitud != null && f.longitud != null && (
            <Link
              href={f.urlMaps || `https://www.google.com/maps?q=${f.latitud},${f.longitud}`}
              isExternal color={IMM_COLOR} fontSize="sm" fontWeight="medium" mt={2} display="inline-block"
            >
              Ver en Google Maps
            </Link>
          )}
        </Box>
      </SimpleGrid>

      <SectionTitle>Información adicional</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Número de hijas/hijos</FormLabel>
          <InputGroup>
            <InputLeftAddon>#</InputLeftAddon>
            <Input type="number" min={0} value={f.numeroHijos ?? ''} onChange={(e) => set('numeroHijos', e.target.value)} />
          </InputGroup>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Contacto de emergencia</FormLabel>
          <Input value={f.contactoEmergenciaNombre || ''} onChange={(e) => set('contactoEmergenciaNombre', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Tel. de emergencia</FormLabel>
          <Input value={f.contactoEmergenciaTelefono || ''} onChange={(e) => set('contactoEmergenciaTelefono', e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 3' }}>
          <FormLabel fontSize="sm">Observaciones</FormLabel>
          <Textarea rows={3} value={f.observaciones || ''} onChange={(e) => set('observaciones', e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">
          Guardar expediente
        </Button>
      </HStack>
    </VStack>
  );
};

export default FormularioExpediente;
